import { AnimatePresence, AnimationDefinition, motion } from "framer-motion";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import { AriaAttributes, useEffect, useRef, useState } from "react";
import "./MegaShareFAB.module.css";
import {
  MegaShareAmount,
  MegaShareBackground,
  MegaShareBonusText,
  MegaShareCoin,
} from "./components";
import {
  ANIMATION_STATE,
  LOOP_CONFIG,
  amountShadowVariant,
  bottomShadowVariant,
  leftCoinVariant,
  midCoinVariant,
  rightCoinVariant,
} from "./utils";

interface MegaShareFABProps extends PropsWithClassName, AriaAttributes {
  zIndex?: number;
  onClick?: () => void;
  initialDelay?: number;
}

const MegaShareFAB = ({
  className,
  zIndex = 10,
  onClick,
  initialDelay = LOOP_CONFIG.INITIAL_DELAY,
  ...ariaProps
}: MegaShareFABProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animState, setAnimState] = useState<ANIMATION_STATE>(
    ANIMATION_STATE.IDLE
  );
  const startTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pulseTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    };
  }, []);

  useEffect(() => {
    // Start the animation loop after the initial delay
    if (!isVisible) {
      if (startTimerRef.current) clearTimeout(startTimerRef.current);
      startTimerRef.current = setTimeout(() => {
        setIsVisible(true);
        setAnimState(ANIMATION_STATE.ENTERING);
      }, initialDelay);
    }
  }, [isVisible, initialDelay]);

  const handleAnimationComplete = (definition: AnimationDefinition) => {
    // Once entering is done, wait, then transition to the infinite pulse
    if (definition === ANIMATION_STATE.ENTERING) {
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      pulseTimerRef.current = setTimeout(() => {
        setAnimState(ANIMATION_STATE.PULSING);
      }, LOOP_CONFIG.PULSE_HOLD_DELAY);
    }
  };

  return (
    <div
      className={cn("relative h-[128px] w-[128px] flex-shrink-0 cursor-pointer", className)}
      style={{ zIndex }}
      onClick={onClick}
      {...ariaProps}
    >
      <AnimatePresence>
        {isVisible && (
          <motion.div
            key="mega-share-content"
            className="absolute inset-0"
            initial={ANIMATION_STATE.IDLE}
            animate={animState}
            exit={ANIMATION_STATE.IDLE}
          >
            <motion.img
              src="/images/mega-share/bottom-shadow.png"
              className="absolute bottom-[10px] left-1/2 -translate-x-1/2 h-[22px] w-[118px]"
              style={{ zIndex: 0 }}
              variants={bottomShadowVariant}
            />

            <MegaShareBackground zIndex={1}>
              <MegaShareCoin
                variant={midCoinVariant}
                zIndex={2}
                className="h-[49px] w-[49px]"
              />
              <MegaShareCoin
                variant={leftCoinVariant}
                zIndex={2}
                className="bottom-[16px] left-[0px] h-[33px] w-[33px]"
              />
              <MegaShareCoin
                variant={rightCoinVariant}
                zIndex={1}
                className="bottom-[16px] right-[0px] h-[33px] w-[33px]"
              />
            </MegaShareBackground>

            <motion.img
              src="/images/mega-share/bonus-amount-shadow.png"
              className="absolute bottom-[22px] left-1/2 -translate-x-1/2 h-[31px] w-[76px]"
              style={{ zIndex: 4 }}
              variants={amountShadowVariant}
            />

            <MegaShareAmount zIndex={5} />

            <MegaShareBonusText
              zIndex={6}
              onAnimationComplete={handleAnimationComplete}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MegaShareFAB;
